/**
 * テストデータパネル
 * 開発環境でのテストデータ注入を支援するUIコンポーネント
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BugIcon, FileTextIcon, RefreshCwIcon, SaveIcon, LoaderIcon } from 'lucide-react';
import { useRepository } from '@/hooks/useRepository';
import { sampleMapData } from '@/data/sampleData';
import { toast } from 'sonner';

/**
 * TestDataPanel props
 */
interface TestDataPanelProps {
  mapId?: string;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  showByDefault?: boolean;
}

/**
 * テストデータパネルコンポーネント
 */
export const TestDataPanel: React.FC<TestDataPanelProps> = ({
  mapId = sampleMapData.id,
  position = 'bottom-right',
  showByDefault = false
}) => {
  const [isOpen, setIsOpen] = useState(showByDefault);
  const [activeTab, setActiveTab] = useState('sample');
  const [customData, setCustomData] = useState<string>('');
  const { mapRepository, testUtils } = useRepository('mock');
  const [lastInjectedData, setLastInjectedData] = useState<string | null>(null);
  
  // 初期表示時にサンプルデータを注入
  useEffect(() => {
    injectSampleData();
  }, []);
  
  // 位置スタイル
  const positionStyles: Record<string, string> = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4'
  };
  
  // サンプルデータを注入
  const injectSampleData = async () => {
    try {
      testUtils.injectMapData(sampleMapData);
      setLastInjectedData('sample');
      toast.success('サンプルデータを注入しました');
    } catch (error) {
      console.error('サンプルデータの注入に失敗:', error);
      toast.error('データ注入に失敗しました');
    }
  };
  
  // カスタムデータを注入
  const injectCustomData = async () => {
    try {
      if (!customData.trim()) {
        toast.error('データを入力してください');
        return;
      }
      
      const data = JSON.parse(customData);
      testUtils.injectMapData(data);
      setLastInjectedData('custom');
      toast.success('カスタムデータを注入しました');
    } catch (error) {
      console.error('カスタムデータの注入に失敗:', error);
      toast.error('不正なJSONデータです');
    }
  };
  
  // データをリセット
  const resetData = () => {
    testUtils.resetTestData();
    setLastInjectedData(null);
    toast.success('データをリセットしました');
  };
  
  // 現在のデータをエクスポート
  const exportCurrentData = async () => {
    try {
      const map = await mapRepository.getMap(mapId);
      if (map) {
        const dataStr = JSON.stringify(map, null, 2);
        setCustomData(dataStr);
        setActiveTab('custom');
        toast.success('現在のデータをエクスポートしました');
      } else {
        toast.error('データが見つかりません');
      }
    } catch (error) {
      console.error('データのエクスポートに失敗:', error);
      toast.error('エクスポートに失敗しました');
    }
  };
  
  // パネルが閉じているとき、開くためのボタンのみ表示
  if (!isOpen) {
    return (
      <div className={`fixed ${positionStyles[position]} z-50`}>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={() => setIsOpen(true)}
          className="bg-background/80 backdrop-blur-sm"
        >
          <BugIcon className="h-4 w-4 mr-2" />
          テストデータ
        </Button>
      </div>
    );
  }
  
  return (
    <div className={`fixed ${positionStyles[position]} z-50 max-w-md`}>
      <Card className="shadow-lg bg-background/90 backdrop-blur-sm">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-sm flex items-center">
              <BugIcon className="h-4 w-4 mr-2" />
              テストデータ注入
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
              ×
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4 p-3">
          {/* 現在の状態 */}
          <div className="flex items-center justify-between">
            <Label>現在のデータ:</Label>
            <Badge variant={lastInjectedData ? "success" : "secondary"}>
              {lastInjectedData === 'sample' ? 'サンプル' : 
               lastInjectedData === 'custom' ? 'カスタム' : 'デフォルト'}
            </Badge>
          </div>
          
          {/* タブインターフェース */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full">
              <TabsTrigger value="sample" className="flex-1">
                <FileTextIcon className="h-4 w-4 mr-2" />
                サンプルデータ
              </TabsTrigger>
              <TabsTrigger value="custom" className="flex-1">
                <FileTextIcon className="h-4 w-4 mr-2" />
                カスタムデータ
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="sample" className="mt-2 space-y-2">
              <div className="text-xs text-muted-foreground">
                {sampleMapData.nodes.length}個のノードと
                {sampleMapData.edges.length}個のエッジを含む事前定義サンプル
              </div>
              <Button 
                size="sm" 
                className="w-full" 
                onClick={injectSampleData}
              >
                サンプルデータを注入
              </Button>
            </TabsContent>
            
            <TabsContent value="custom" className="mt-2 space-y-2">
              <Textarea
                placeholder="JSONデータを入力"
                className="min-h-[150px] text-xs font-mono"
                value={customData}
                onChange={(e) => setCustomData(e.target.value)}
              />
              <div className="flex space-x-2">
                <Button 
                  size="sm" 
                  className="flex-1" 
                  onClick={injectCustomData}
                >
                  <SaveIcon className="h-4 w-4 mr-2" />
                  カスタムデータを注入
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  className="flex-initial"
                  onClick={exportCurrentData}
                >
                  <LoaderIcon className="h-4 w-4" />
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
        
        <CardFooter className="p-3 pt-0 flex justify-between">
          <Button 
            size="sm" 
            variant="ghost" 
            className="text-xs" 
            onClick={resetData}
          >
            <RefreshCwIcon className="h-3 w-3 mr-1" />
            リセット
          </Button>
          
          <span className="text-xs text-muted-foreground">開発環境のみ有効</span>
        </CardFooter>
      </Card>
    </div>
  );
};

export default TestDataPanel;